import { useLocation, useNavigate } from 'react-router-dom'
import { PageTitle, TabBody } from '../../components/PageTitle'
import { activeTabFor, screenFor, tabsFor } from '../../lib/screens'
import { withTab } from '../../lib/tabs'
import { session } from '../../lib/api'
import { AppearanceCard } from './AppearanceCard'
import { DashboardCard } from './DashboardCard'
import { PasswordCard } from './PasswordCard'
import { ProviderCredentialCard } from './ProviderCredentialCard'

/**
 * The four things that belong to a person rather than to the fleet, one per tab.
 *
 * They were four cards stacked on one page, which worked while there were two of them and stopped
 * working at four: they share nothing but a screen. Changing a password, choosing a palette,
 * arranging a dashboard and setting the deployment's provider key are four separate errands, and
 * somebody scrolling past three of them to reach the fourth is reading a list rather than using a
 * screen. The Devices tab host makes the same argument at greater length (D-429).
 *
 * **Appearance is the default tab, and that reverses the order the cards were in.** On one page the
 * password went first, because it was the only card with a security consequence. A default tab is
 * not a first card: `/settings` is where `homeRouteFor` sends somebody whose roles can reach nothing
 * else, and landing them on a password form reads as being asked to change it. Appearance is the
 * harmless one and the one most people come for, so it is what the bare path shows; the password is
 * an errand somebody navigates to.
 *
 * The Provider tab is hidden from anybody but an owner, which the tab list must do rather than the
 * card. `ProviderCredentialCard` renders nothing for a non-owner - the right answer when it was one
 * card among four, and an empty panel now that it is a tab of its own. The gate is declared beside
 * the tab in `lib/screens.ts` and applied through the same `permits` the navigation uses, so this
 * screen, the rail and the help panel cannot come to disagree about what an owner is (D-493).
 */
const SCREEN = screenFor('/settings')

export function SettingsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const roles = session.identity()?.roles ?? []

  // Both of these resolve against the tabs this person actually has, which is the order rather than
  // an implementation detail: a pasted `?tab=provider` an analyst cannot open has to fall back to
  // Appearance rather than select a panel with nothing in it. `activeTabFor` filters before it asks
  // `tabFor`, so the two cannot come apart the way two separate calls here could.
  const tabs = tabsFor(SCREEN, roles)
  const tab = activeTabFor(SCREEN, location.search, roles)

  return (
    <div className="tabbed">
      <div className="page-header">
        <div>
          <PageTitle>Settings</PageTitle>
          <p>Your password, how this console looks to you, and how it reaches the model provider.</p>
        </div>
      </div>

      <div className="tab-strip" role="tablist" aria-label={SCREEN.title}>
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`settings-tab-${key}`}
            aria-selected={tab === key}
            aria-controls="settings-tab-panel"
            onClick={() =>
              navigate(`/settings${key === SCREEN.defaultTab ? '' : withTab(location.search, key)}`)
            }
            data-testid={`tab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      <div id="settings-tab-panel" role="tabpanel" aria-labelledby={`settings-tab-${tab}`} tabIndex={-1}>
        <TabBody>
          {tab === 'password' ? (
            <PasswordCard />
          ) : tab === 'dashboard' ? (
            <DashboardCard />
          ) : tab === 'provider' ? (
            <ProviderCredentialCard />
          ) : (
            <AppearanceCard />
          )}
        </TabBody>
      </div>
    </div>
  )
}
